import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FrontSelect } from '../frontSelect/FrontSelect';
import { MarketplaceValueContainer } from './components/MarketplaceValueContainer';
import { marketplaceGroups, marketplaceGroupsAmazon } from './helpers/const';
import { getSelectedMarketplaceGroupValue, getSelectMarketplaceIdValues } from './helpers/helpers';
import type { IMarketplace } from './helpers/types';

interface IProps {
  onChange: (option: string[]) => void;
  value: string[];
  pageMarketplace: boolean | 'All' | 'Amazon';
}

export const MarketplaceSelect = ({ value, onChange, pageMarketplace }: IProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const handleMenuOpen = () => {
    setMenuIsOpen(true);
  };

  const handleMenuClose = () => {
    setMenuIsOpen(false);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      handleMenuClose();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const onChangeHandler = useCallback(
    (option: IMarketplace[]) => {
      if (option?.[option?.length - 1]?.value === 'All') {
        option = [];
      }

      onChange(getSelectMarketplaceIdValues(option));
    },
    [onChange],
  );

  const options = useMemo((): IMarketplace[] => {
    if (pageMarketplace === false || pageMarketplace === 'All') {
      return [marketplaceGroups[0]];
    } else if (pageMarketplace === 'Amazon') {
      return marketplaceGroupsAmazon;
    } else {
      return marketplaceGroups;
    }
  }, [pageMarketplace]);

  const values = useMemo(() => {
    const val = getSelectedMarketplaceGroupValue(options, value);

    return pageMarketplace === 'Amazon'
      ? val.filter((e) => !['Walmart', 'TikTok'].includes(e.value))
      : val;
  }, [options, pageMarketplace, value]);

  useEffect(() => {
    onChangeHandler(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMarketplace]);

  return (
    <div ref={selectRef}>
      <FrontSelect
        width={'200px'}
        placeholder="Marketplace"
        defaultValue={values}
        value={values}
        options={options}
        getOptionLabel={(option) => `${option.flag ?? ''} ${option.label}`}
        getOptionValue={(option) => option.value}
        onChange={(option) => onChangeHandler([...option])}
        isSearchable={false}
        isClearable={true}
        isMulti={true}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        noOptionsMessage={() => 'No Marketplace Available'}
        components={{
          ValueContainer: MarketplaceValueContainer,
        }}
      />
    </div>
  );
};
